import { GetStaticProps, InferGetStaticPropsType, NextPage } from 'next';
import { useRouter } from 'next/router';
import shallow from 'zustand/shallow';

import { AuthForm } from 'src/authentication/templates/AuthForm/AuthForm';
import {
  onCheckPhoneNumberTaken,
  onResetPassword,
  onSendVerificationToken,
  onSubmitEmail,
} from 'src/authentication/templates/AuthForm/AuthForm.helpers';
import {
  AuthFormDisplayState,
  AuthFormDisplayStates,
  AuthFormErrors,
} from 'src/authentication/templates/AuthForm/AuthForm.types';
import { ContentfulCarmaError } from 'src/data/Contentful/ContentfulGetCarmaErrorListing';
import { ContentfulMicrocopy } from 'src/data/Contentful/ContentfulGetMicrocopyListing';
import { getPageDefaults } from 'src/data/StaticPageGeneration/PageDefaults';
import { getPagePlpDefaults } from 'src/data/StaticPageGeneration/PagePlpDefaults';
import { SeoMeta } from 'src/general/components/SeoMeta/SeoMeta';
import { flattenQueryParam } from 'src/general/helpers/flattenQueryParam';
import authService from 'src/services/AuthService';
import { authStore } from 'src/stores/authStore';
import { useCarmaError } from 'src/stores/errorStore';
import { PlpStoreState } from 'src/stores/plpStoreLegacy';
import { formatPhoneForPayload } from 'src/utils/formUtils';
import { getOr } from 'src/utils/getOr';

const Signin: NextPage<InferGetStaticPropsType<typeof getStaticProps>> = () => {
  const router = useRouter();
  const getErrorMessage = useCarmaError();
  const { origin, redirect, type } = router.query;
  const { setUserAuthData } = authStore((state) => ({ setUserAuthData: state.setUserAuthData }), shallow);
  const sendUserBack = () => ((origin as string) ? (window.location.href = origin as string) : handleBackButtonClick());

  const handleBackButtonClick = () => {
    if (document.referrer === '') {
      router.push('/');
    } else {
      router.back();
    }
  };

  const onSignin = async (email: string, password: string) => {
    try {
      const result = await authService.signIn({ username: email, password });
      setUserAuthData({ id: result.data.customer_id, accessToken: result.data.access_token }, false);
    } catch (error) {
      return handleApiErrorMessage(error, 'Failed to login, please try again.');
    }

    return true;
  };

  // Ensure we have an authorised display state
  const rawDisplayState = (flattenQueryParam(type) as AuthFormDisplayState) ?? 'default';
  const displayState: AuthFormDisplayState =
    AuthFormDisplayStates.indexOf(rawDisplayState) >= 0 ? rawDisplayState : 'default';
  const redirectUrl = flattenQueryParam(redirect);

  return (
    <>
      <SeoMeta seoPageTitle="Login or sign up | Carma" />
      <AuthForm
        redirectUrl={redirectUrl}
        displayState={displayState}
        onCancel={sendUserBack}
        onSuccess={async () => {
          // Force load
          window.location.href = redirect ? (redirect as string) : '/';
        }}
        onSubmitEmail={onSubmitEmail}
        onSignin={onSignin}
        onResetPassword={onResetPassword}
        onCheckPhoneNumberTaken={onCheckPhoneNumberTaken}
        onSendVerificationToken={onSendVerificationToken}
        onSignup={async ({
          email,
          firstName,
          lastName,
          phone,
          password,
          marketing,
          verificationId,
          verificationCode,
        }) => {
          let result: string | boolean = true;
          const formattedPhone = formatPhoneForPayload(phone);
          try {
            await authService.signUp({
              phone: formattedPhone,
              email,
              firstName,
              lastName,
              marketing,
              password,
              verification: {
                verification_code: verificationCode,
                verification_id: verificationId,
              },
            });
          } catch (error: any) {
            const { error_key: key, error_message: message } = getOr(error as any, 'response.data', {});
            const errorMessage = getErrorMessage(key, message, 'Failed to sign-up, please try again later.');
            return errorMessage;
          }
          try {
            result = await onSignin(email, password);
          } catch (error) {
            return 'Unknown error happened';
          }
          return result;
        }}
      />
    </>
  );
};

export const handleApiErrorMessage = (error: unknown, defaultErrorMessage: string): string => {
  const errorCode: number = getOr(error as any, 'response.data.error_code', 0);
  switch (errorCode) {
    case AuthFormErrors.PENDING_VERIFICATION:
      return 'Invalid verification code provided.';
    case AuthFormErrors.ACCOUNT_NOT_VALIDATED:
      return 'The account is not verified, please contact customer support for assistance.';
    case AuthFormErrors.PASSWORD_INVALID:
      return 'Password incorrect, please try again.';
    default:
      return defaultErrorMessage;
  }
};

export default Signin;

export const getStaticProps: GetStaticProps<{
  contentfulMicrocopy: ContentfulMicrocopy[];
  contentfulCarmaError: ContentfulCarmaError[];
  initialPlpStoreState: Partial<PlpStoreState>;
  preview: boolean;
}> = async (context) => {
  const preview = !!context?.preview;
  const [{ contentfulMicrocopy, contentfulCarmaError }, { filterData, plpPagesMap }] = await Promise.all([
    getPageDefaults(preview),
    getPagePlpDefaults(preview),
  ]);

  const initialPlpStoreState: Partial<PlpStoreState> = {
    filterData,
    plpPagesMap,
  };

  return {
    props: {
      contentfulMicrocopy,
      contentfulCarmaError,
      initialPlpStoreState,
      preview,
    },
    revalidate: 5 * 60, // Revalidate every 5 minutes
  };
};
